import axios from 'axios';
import { auth } from './firebaseClient';

const getApiUrl = () => {
  const prodUrl = 'https://cre-dominion-backend-1006317416349.us-central1.run.app';
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:8000';
    }
  }
  return prodUrl;
};

const api = axios.create({
  baseURL: getApiUrl(),
});

// Add a request interceptor to add the Firebase ID token to the headers
api.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle credit depletion events globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response && (error.response.status === 402 || 
        (error.response.data && typeof error.response.data.detail === 'string' && 
         error.response.data.detail.toLowerCase().includes('insufficient credits')))) {
      if (typeof window !== 'undefined') {
        const event = new CustomEvent('credits-depleted');
        window.dispatchEvent(event);
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// --- Helper Functions to match components expectations ---
// Each helper now returns .data directly to match previous Supabase client behavior

export const getDashboardData = (limit?: number) => api.get(`/dashboard${limit ? `?limit=${limit}` : ''}`).then(r => r.data);
export const getPaymentsData = (startDate?: string, endDate?: string) => {
    const params = (startDate && endDate) ? { start_date: startDate, end_date: endDate } : {};
    return api.get('/calendar/payments', { params }).then(r => r.data);
};
export const searchDocuments = (query: string) => api.post('/documents/search', { query }).then(r => r.data);
export const getProperties = () => api.get('/properties').then(r => r.data);
export const getTenants = () => api.get('/tenants').then(r => r.data);
export const getProperty = (id: string) => api.get(`/properties/${id}`).then(r => r.data);
export const getUserSettings = () => api.get('/account/settings').then(r => r.data);
export const updateUserSettings = (settings: any) => api.post('/account/settings', settings).then(r => r.data);
export const createCheckoutSession = (plan: string, redirectUrl?: string) => api.post('/subscription/create-checkout', { plan, redirectUrl }).then(r => r.data);
export const redeemCoupon = (code: string) => api.post('/coupon/redeem', { code }).then(r => r.data);
export const getStripeConfig = () => api.get('/subscription/config').then(r => r.data);

// Market Analysis
export const getMarketAnalysis = (params: any) => api.post('/analysis/market', params).then(r => r.data);
export const saveMarketAnalysisResult = (propertyId: string, result: any) => api.post(`/analysis/market/save/${propertyId}`, result).then(r => r.data);
export const getSavedMarketAnalysisResult = (id: string) => api.get(`/analysis/market/${id}`).then(r => r.data);

// Valuation
export const getPropertyValuation = (propertyId: string, valuationMethod: string) => api.post('/analysis/valuation', { property_id: propertyId, valuation_method: valuationMethod }).then(r => r.data);
export const savePropertyValuationResult = (propertyId: string, result: any) => api.post(`/analysis/valuation/save/${propertyId}`, result).then(r => r.data);
export const getSavedPropertyValuationResult = (id: string) => api.get(`/analysis/valuation/${id}`).then(r => r.data);

// Predictive Trends
export const getPredictiveTrends = (propertyId: string, date: string, propData: any) => api.post('/analysis/trends', { property_id: propertyId, date, prop_data: propData }).then(r => r.data);
export const savePredictiveTrendsResult = (result: any) => api.post('/analysis/trends/save', result).then(r => r.data);
export const getSavedPredictiveTrendsResult = (id: string) => api.get(`/analysis/trends/${id}`).then(r => r.data);

// Lease Abstraction
export const getSavedLeaseAbstractionResult = (propertyId: string, leaseId: string) => api.get(`/leases/abstraction/${propertyId}/${leaseId}`).then(r => r.data);

// Blog
let _blogMetadataCache: any[] | null = null;
export const getBlogArticlesMetadata = async (forceRefresh = false) => {
  if (!forceRefresh && _blogMetadataCache && _blogMetadataCache.length > 0) {
    return _blogMetadataCache;
  }
  const data = await api.get('/blog/metadata').then(r => r.data);
  if (Array.isArray(data) && data.length > 0) {
    _blogMetadataCache = data;
  }
  return data;
};

// Generic wrappers to handle common patterns
export const get = (url: string) => api.get(url).then(r => r.data);
export const post = (url: string, data?: any) => api.post(url, data).then(r => r.data);
export const put = (url: string, data?: any) => api.put(url, data).then(r => r.data);
export const del = (url: string) => api.delete(url).then(r => r.data);
